import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '../api'
import { formatWhen, t } from '../i18n'
import { haptic, notify, openExternal, shareToChat } from '../telegram'
import { isTournamentKind } from '../types'
import type { Game, InviteCandidate, PlayerMe } from '../types'

interface Props {
  game: Game
  me: PlayerMe
  onClose: () => void
  /** Кого-то позвали — экран игры обновит список приглашённых */
  onInvited: () => void
}

function initials(name: string): string {
  return name.trim().slice(0, 1).toUpperCase() || '?'
}

/** Личная ссылка: та же ссылка на игру + кто позвал. Человек не из
 *  приложения пройдёт онбординг и попадёт в эту игру, а пригласивший
 *  получит сообщение, когда тот запишется. */
function personalLink(game: Game, me: PlayerMe): string | null {
  return game.share_url ? `${game.share_url}_i${me.id}` : null
}

/** То же для WhatsApp: страница на нашем домене. Ссылку t.me, открытую из
 *  WhatsApp, Telegram часто показывает как чат с ботом, а не как игру. */
function personalWebLink(game: Game, me: PlayerMe): string | null {
  return game.web_url ? `${game.web_url}_i${me.id}` : personalLink(game, me)
}

export default function InviteSheet({ game, me, onClose, onInvited }: Props) {
  const [q, setQ] = useState('')
  const [people, setPeople] = useState<InviteCandidate[] | null>(null)
  const [busy, setBusy] = useState<number | null>(null)
  const request = useRef(0)

  const load = useCallback(
    async (query: string) => {
      // Ответы на быстрый набор приходят не по порядку — берём только последний
      const id = ++request.current
      try {
        const found = await api.inviteCandidates(game.id, query)
        if (id === request.current) setPeople(found)
      } catch (e) {
        if (id === request.current) {
          setPeople([])
          notify(e instanceof Error ? e.message : 'Ошибка')
        }
      }
    },
    [game.id],
  )

  useEffect(() => {
    const timer = setTimeout(() => void load(q.trim()), q ? 250 : 0)
    return () => clearTimeout(timer)
  }, [q, load])

  // Под листом страница не должна прокручиваться
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  async function invite(person: InviteCandidate) {
    setBusy(person.id)
    haptic('medium')
    try {
      const created = await api.invite(game.id, person.id)
      setPeople((list) =>
        (list ?? []).map((p) => (p.id === person.id ? { ...p, invite_status: created.status } : p)),
      )
      onInvited()
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setBusy(null)
    }
  }

  const link = personalLink(game, me)
  const text = t().inviteLinkText(formatWhen(game.starts_at), game.club.name, isTournamentKind(game.kind))
  const full = game.joined_count >= game.max_players

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={t().inviteTitle} onClick={(e) => e.stopPropagation()}>
        <div className="row between">
          <div className="title">{t().inviteTitle}</div>
          <button className="ghost" onClick={onClose}>
            {t().doneClose}
          </button>
        </div>
        <div className="muted small" style={{ margin: '6px 0 10px' }}>
          {full ? t().inviteHintFull : t().inviteHint}
        </div>

        <input
          type="search"
          placeholder={t().inviteSearch}
          aria-label={t().inviteSearch}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <div className="sheet-list">
          {people === null && <p className="muted">{t().loading}</p>}
          {people?.length === 0 && <p className="muted">{t().noCandidates}</p>}
          {people?.map((p) => (
            <div key={p.id} className="row between" style={{ padding: '8px 0', gap: 10 }}>
              <div className="row" style={{ gap: 10, minWidth: 0 }}>
                {p.photo_url ? (
                  <img className="avatar" style={{ marginLeft: 0 }} src={p.photo_url} alt="" />
                ) : (
                  <span className="avatar" style={{ marginLeft: 0 }}>
                    {initials(p.first_name)}
                  </span>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{p.first_name}</div>
                  <div className="muted small">
                    {t().level} {p.level.toFixed(2)}
                    {p.played_together ? ` · ${t().playedTogether}` : ''}
                    {!p.fits_level ? ` · ${t().levelOff}` : ''}
                  </div>
                </div>
              </div>
              {p.invite_status === 'declined' ? (
                <span className="badge warn">{t().declinedMark}</span>
              ) : p.invite_status ? (
                <span className="badge">✓ {t().invitedMark}</span>
              ) : (
                <button className="invite-btn" disabled={busy === p.id} onClick={() => invite(p)}>
                  {t().inviteBtn}
                </button>
              )}
            </div>
          ))}
        </div>

        {link && (
          <div className="sheet-foot">
            <div style={{ fontWeight: 600 }}>{t().notInApp}</div>
            <div className="muted small" style={{ margin: '2px 0 8px' }}>
              {t().notInAppHint}
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button className="ghost" style={{ flex: 1 }} onClick={() => shareToChat(link, text)}>
                {t().viaTelegram}
              </button>
              <button
                className="ghost"
                style={{ flex: 1 }}
                onClick={() =>
                  openExternal(`https://wa.me/?text=${encodeURIComponent(`${text}: ${personalWebLink(game, me)}`)}`)
                }
              >
                {t().viaWhatsApp}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
